import React from "react";
import "../styles/calendar.css";
import { sanitizeAndLinkifyDescription } from "../utils/htmlSanitizer";

export type GoogleEventDetails = {
    id: string;
    summary: string;
    description?: string;
    location?: string;
    start: number;
    end: number;
    attendees?: Array<{
        email: string;
        displayName?: string;
        optional: boolean;
        responseStatus: string;
    }>;
    creator?: string;
    organizer?: string;
    status?: string;
    htmlLink?: string;
};

type GoogleEventDetailsModalProps = {
    event: GoogleEventDetails | null;
    loading: boolean;
    onClose: () => void;
};

export default function GoogleEventDetailsModal({
    event,
    loading,
    onClose,
}: GoogleEventDetailsModalProps) {
    const formatDate = (timestamp: number) => {
        const date = new Date(timestamp);
        return date.toLocaleString("en-US", {
            weekday: "long",
            year: "numeric",
            month: "long",
            day: "numeric",
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
        });
    };

    const getResponseStatusBadge = (status: string) => {
        const statusMap: Record<string, { label: string; className: string }> = {
            accepted: { label: "Yes", className: "status-accepted" },
            declined: { label: "No", className: "status-declined" },
            tentative: { label: "Maybe", className: "status-tentative" },
            needsAction: { label: "Awaiting", className: "status-needs-action" },
        };
        return statusMap[status] || { label: status, className: "" };
    };

    return (
        <div className="cal-modal" role="dialog" aria-modal="true" aria-labelledby="google-event-modal-title">
            <div className="cal-modal-card google-event-modal">
                <div className="cal-modal-head">
                    <h3 id="google-event-modal-title">
                        {loading ? "Loading..." : event?.summary || "Event Details"}
                    </h3>
                    <button className="cal-x" onClick={onClose} aria-label="Close">
                        ×
                    </button>
                </div>

                {loading ? (
                    <div className="cal-modal-body">
                        <p>Loading event details...</p>
                    </div>
                ) : event ? (
                    <div className="cal-modal-body google-event-details">
                        {/* Date & Time */}
                        <div className="event-detail-section">
                            <div className="event-detail-icon">📅</div>
                            <div className="event-detail-content">
                                <div className="event-time">
                                    {formatDate(event.start)} – {formatDate(event.end)}
                                </div>
                            </div>
                        </div>

                        {/* Location */}
                        {event.location && (
                            <div className="event-detail-section">
                                <div className="event-detail-icon">📍</div>
                                <div className="event-detail-content">
                                    <div className="event-location">{event.location}</div>
                                </div>
                            </div>
                        )}

                        {/* Description */}
                        <div className="event-detail-section">
                            <div className="event-detail-icon">📝</div>
                            <div className="event-detail-content">
                                <div
                                    className={`event-description${!event.description ? " event-description-placeholder" : ""}`}
                                    dangerouslySetInnerHTML={{ __html: sanitizeAndLinkifyDescription(event.description) }}
                                />
                            </div>
                        </div>

                        {/* Attendees */}
                        {event.attendees && event.attendees.length > 0 && (
                            <div className="event-detail-section">
                                <div className="event-detail-icon">👥</div>
                                <div className="event-detail-content">
                                    <div className="event-attendees-label">
                                        {event.attendees.length} guest{event.attendees.length !== 1 ? "s" : ""}
                                    </div>
                                    <ul className="event-attendees-list">
                                        {event.attendees.map((attendee, idx) => {
                                            const statusBadge = getResponseStatusBadge(attendee.responseStatus);
                                            return (
                                                <li key={idx} className="event-attendee">
                                                    <div className="attendee-info">
                                                        <span className="attendee-name">
                                                            {attendee.displayName || attendee.email}
                                                        </span>
                                                        {attendee.optional && (
                                                            <span className="attendee-optional">Optional</span>
                                                        )}
                                                    </div>
                                                    <span className={`attendee-status ${statusBadge.className}`}>
                                                        {statusBadge.label}
                                                    </span>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>
                            </div>
                        )}

                        {/* Organizer */}
                        {event.organizer && (
                            <div className="event-detail-section">
                                <div className="event-detail-icon">👤</div>
                                <div className="event-detail-content">
                                    <div className="event-organizer">
                                        <span className="event-label">Organizer: </span>
                                        {event.organizer}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* View in Google Calendar */}
                        {event.htmlLink && (
                            <div className="event-detail-section">
                                <a
                                    href={event.htmlLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn-view-google"
                                >
                                    View in Google Calendar
                                </a>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="cal-modal-body">
                        <p>Failed to load event details.</p>
                    </div>
                )}

                <div className="cal-modal-actions">
                    <button className="btn-pill" onClick={onClose}>
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
}
